import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Stack,
  Pagination,
  Chip,
  Tooltip,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import ImageViewerDialog from '../ImageViewerDialog';
import { createConcurrencyLimiter } from '../../../utils/concurrentLimit';

interface PlateSighting {
  id: string;
  deviceEventId: string;
  cameraDeviceId: string;
  plate: string;
  confidence: number;
  reads: number;
  alternatives: string[];
  firstSeen: string;
  lastSeen: string;
  direction: string | null;
  siteName: string;
  cameraName: string;
  frameFileId: string | null;
  cropFileId: string | null;
}

interface PlateSightingsTabProps {
  deviceId: string;
  includeChildren?: boolean;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const toUtcIso = (value: string): string => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

const fileUrl = (s: PlateSighting, fileId: string, maxWidth?: number) =>
  `/api/devices/${s.cameraDeviceId}/events/${s.deviceEventId}/images/${fileId}/content${maxWidth ? `?maxWidth=${maxWidth}` : ''}`;

const confidenceColor = (c: number): 'success' | 'warning' | 'error' =>
  c >= 0.8 ? 'success' : c >= 0.5 ? 'warning' : 'error';

const PlateSightingsTab: React.FC<PlateSightingsTabProps> = ({ deviceId, includeChildren = false }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const [sightings, setSightings] = useState<PlateSighting[]>([]);
  const [crops, setCrops] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plateSearch, setPlateSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [total, setTotal] = useState(0);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerTitle, setViewerTitle] = useState('');

  const authHeader = { Authorization: `Bearer ${accessToken}` };

  const loadSightings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        skip: ((page - 1) * pageSize).toString(),
        take: pageSize.toString(),
      });
      if (plateSearch.trim()) params.append('plate', plateSearch.trim());
      const startUtc = toUtcIso(startDate);
      const endUtc = toUtcIso(endDate);
      if (startUtc) params.append('startDate', startUtc);
      if (endUtc) params.append('endDate', endUtc);
      if (includeChildren) params.append('includeChildren', 'true');

      const res = await fetch(`/api/devices/${deviceId}/plate-sightings?${params}`, { headers: authHeader });
      if (!res.ok) throw new Error('Failed to load plate sightings');
      const data = await res.json();
      const rows: PlateSighting[] = data.data || [];
      setSightings(rows);
      setTotal(data.total || 0);

      // Plate crops are small; fetch them as blobs since the image endpoints need the auth header
      const limit = createConcurrencyLimiter(8);
      const results = await Promise.allSettled(
        rows.filter((s) => s.cropFileId).map((s) => limit(async () => {
          const r = await fetch(fileUrl(s, s.cropFileId!, 240), { headers: authHeader });
          if (!r.ok) return null;
          return [s.id, URL.createObjectURL(await r.blob())] as const;
        }))
      );
      setCrops((prev) => {
        Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
        const next: Record<string, string> = {};
        results.forEach((r) => {
          if (r.status === 'fulfilled' && r.value) next[r.value[0]] = r.value[1];
        });
        return next;
      });
    } catch (e: any) {
      setError(e.message || 'Failed to load plate sightings');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, includeChildren, page, pageSize, plateSearch, startDate, endDate, accessToken]);

  useEffect(() => {
    const t = setTimeout(loadSightings, 300); // debounce plate typing
    return () => clearTimeout(t);
  }, [loadSightings]);

  useEffect(() => () => {
    Object.values(crops).forEach((u) => URL.revokeObjectURL(u));
    if (viewerUrl) URL.revokeObjectURL(viewerUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openFrame = async (s: PlateSighting) => {
    const fileId = s.frameFileId ?? s.cropFileId;
    if (!fileId) return;
    try {
      const r = await fetch(fileUrl(s, fileId), { headers: authHeader });
      if (!r.ok) throw new Error();
      if (viewerUrl) URL.revokeObjectURL(viewerUrl);
      setViewerUrl(URL.createObjectURL(await r.blob()));
      setViewerTitle(`${s.plate || 'Unread plate'} — ${s.cameraName} — ${new Date(s.firstSeen).toLocaleString()}`);
      setViewerOpen(true);
    } catch {
      setError('Failed to load image');
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Plate"
          size="small"
          value={plateSearch}
          onChange={(e) => { setPlateSearch(e.target.value.toUpperCase()); setPage(1); }}
          placeholder="e.g. QGNR31"
        />
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          label="Per page"
          size="small"
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          sx={{ minWidth: 100 }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </TextField>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading && sightings.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : sightings.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3 }}>No plate sightings found.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plate</TableCell>
                <TableCell>Read</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Images</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Camera</TableCell>
                <TableCell>Seen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sightings.map((s) => (
                <TableRow key={s.id} hover sx={{ cursor: 'pointer' }} onClick={() => openFrame(s)}>
                  <TableCell>
                    {crops[s.id] ? (
                      <Box component="img" src={crops[s.id]} alt={s.plate} sx={{ height: 40, maxWidth: 160, borderRadius: 0.5, display: 'block' }} />
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', letterSpacing: 1 }}>
                      {s.plate || <Typography component="span" color="text.secondary" fontStyle="italic">unread</Typography>}
                    </Typography>
                    {s.alternatives.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        or {s.alternatives.join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={confidenceColor(s.confidence)} label={`${Math.round(s.confidence * 100)}%`} />
                  </TableCell>
                  <TableCell>{s.reads}</TableCell>
                  <TableCell>{s.direction ? <Chip size="small" label={s.direction.toUpperCase()} /> : '—'}</TableCell>
                  <TableCell>{s.cameraName}</TableCell>
                  <TableCell>
                    <Tooltip title={`Last seen ${new Date(s.lastSeen).toLocaleString()}`}>
                      <span>{new Date(s.firstSeen).toLocaleString()}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {total > pageSize && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={Math.ceil(total / pageSize)} page={page} onChange={(_, p) => setPage(p)} />
        </Box>
      )}

      <ImageViewerDialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        imageUrl={viewerUrl}
        eventName={viewerTitle}
      />
    </Box>
  );
};

export default PlateSightingsTab;
